<template>
    <div class="nch-profile mb-7 mt-4">
        <div class="container">
            <header class="nch-profile__pagehead">
                <div>
                    <h1 class="nch-profile__pagetitle">{{ $t('profile.info') }}</h1>
                    <p class="nch-profile__pagesub">{{ owner.name }} · {{ owner.email }}</p>
                </div>
                <base-button
                    type="success"
                    class="nch-profile__save"
                    :disabled="disableSubmitButton"
                    @click="submit"
                >
                    <i class="fas fa-check mr-2"></i>{{ $t('common.save') }}
                </base-button>
            </header>

            <div class="row">
                <div class="col-12 col-lg-8">
                    <section class="nch-profile__card">
                        <div class="nch-profile__cardhead">
                            <i class="fas fa-user-circle"></i>
                            <span>{{ $t('profile.info') }}</span>
                        </div>
                        <div class="nch-profile__cardbody">
                        <tabs
                            tabNavClasses="nav-fill flex-column flex-sm-row nav-wrapper"
                            :color="color.primary"
                        >
                            <tab-pane id="owner" class="mt-3">
                                <template #title><span
                                    class="nch--tab font-weight-bold"
                                >
                                    {{ $t('profile.owner') }}
                                </span></template>
                                <profile-owner
                                    v-model="owner"
                                    :blockStudentFields="blockStudentFields"
                                    @changePassword="changePassword"
                                    :color="color"
                                    @errors="(val) => (errorsOwner = val)"
                                />
                            </tab-pane>
                            <tab-pane
                                v-if="data.secondary_limit > 0"
                                id="secondary"
                                class="mt-3"
                            >
                                <template #title><span
                                    class="nch--tab font-weight-bold"
                                >
                                    {{ $t('profile.secondary.title') }}
                                </span></template>
                                <base-button
                                    v-if="
                                        data.secondary_limit >
                                        data.secondarys.length
                                    "
                                    class="mb-4"
                                    block
                                    type="success"
                                    @click="createNewSecondary"
                                >
                                    {{ $t('profile.secondary.new_secondary') }}
                                </base-button>
                                <profile-secondary
                                    class="mb-5"
                                    v-for="secondary in secondarys"
                                    :model-value="secondary"
                                    :key="secondary.id"
                                    @changePassword="changePassword"
                                    @removeSecondaryUser="removeUser"
                                    :blockStudentFields="blockStudentFields"
                                    :color="color"
                                    @errors="countErrorsSecondary"
                                />
                            </tab-pane>
                        </tabs>
                        </div>
                    </section>

                    <section v-if="data.form.fields.length > 0" class="nch-profile__card">
                        <div class="nch-profile__cardhead">
                            <i class="fas fa-list-alt"></i>
                            <span>{{ $t('profile.additional_info') }}</span>
                        </div>
                        <div class="nch-profile__cardbody">
                            <form-builder v-model="formsNew" profile />
                        </div>
                    </section>

                    <section v-if="form.enable_addresses" class="nch-profile__card">
                        <div class="nch-profile__cardhead">
                            <i class="fas fa-map-marker-alt"></i>
                            <span>{{ $t('common.location.addresses') }}</span>
                        </div>
                        <div class="nch-profile__cardbody">
                        <div
                            v-for="(address, i) in addresses"
                            :key="address.id"
                        >
                            <address-form
                                v-if="edit && addressId === address.id"
                                profile
                                v-model="addresses[i]"
                            />
                            <profile-addresses v-else :address="address" />

                            <div
                                class="d-flex flex-wrap justify-content-between"
                            >
                                <div class="col-md-6 mb-4 pl-0">
                                    <base-button
                                        block
                                        v-if="edit"
                                        type="warning"
                                        @click="edit = false"
                                    >
                                        {{ $t('common.cancel') }}
                                    </base-button>
                                    <base-button
                                        v-else
                                        block
                                        type="danger"
                                        :disabled="disbaleRemoveAddress"
                                        @click="removeAddress(address.id)"
                                    >
                                        {{ $t('common.remove') }}
                                    </base-button>
                                </div>
                                <div class="col-md-6 pr-0">
                                    <base-button
                                        block
                                        v-if="!edit"
                                        type="info"
                                        @click="editAddress(address.id)"
                                    >
                                        {{ $t('common.edit') }}
                                    </base-button>
                                    <base-button
                                        block
                                        type="success"
                                        v-else-if="addressId === address.id"
                                        @click="saveAddress(addresses[i])"
                                    >
                                        {{ $t('common.save') }}
                                    </base-button>
                                </div>
                            </div>
                        </div>
                        <base-button
                            block
                            type="success"
                            @click="newAddress = true"
                        >
                            {{ $t('common.add') }}
                        </base-button>

                        <div class="d-flex justify-content-center">
                            <base-pagination
                                v-if="meta.total > 3"
                                class="mt-4"
                                :page-count="meta.last_page"
                                v-model="meta.current_page"
                                @input="onChangePage"
                                type="#525F7F"
                            />
                        </div>
                        </div>
                    </section>
                </div>

                <div class="col-12 col-lg-4">
                    <section class="nch-profile__card nch-profile__card--center">
                        <div class="nch-profile__cardhead">
                            <i class="fas fa-camera"></i>
                            <span>{{ $t('profile.photo') }}</span>
                        </div>
                        <div class="nch-profile__cardbody">
                            <photo-selector
                                :photo="profile.upload"
                                @changePhoto="changePhoto"
                            />
                        </div>
                    </section>

                    <section class="nch-profile__card">
                        <div class="nch-profile__cardhead">
                            <i class="fas fa-id-card"></i>
                            <span>{{ $t('common.information') }}</span>
                        </div>
                        <div class="nch-profile__cardbody">
                        <template
                            v-if="
                                addresses.length &&
                                addresses[0].student[0].categories.length
                            "
                        >
                            <span> {{ $t('profile.category') }}</span>
                            <base-input
                                v-model="
                                    addresses[0].student[0].categories[0]
                                        .translation.name
                                "
                                name="categoria"
                                disabled
                                placeholder="Categoria"
                            />
                        </template>
                        <document-input
                            v-model="student"
                            :blockStudentFields="blockStudentFields"
                        />

                        <base-input
                            v-model="profile.facebook"
                            v-validate="'url'"
                            prepend-icon="fab fa-facebook-f"
                            name="facebook"
                            placeholder="Facebook"
                            :error="errors.first('facebook')"
                        />

                        <base-input
                            v-model="profile.instagram"
                            prepend-icon="fab fa-instagram"
                            name="instagram"
                            placeholder="Instagram"
                            :error="errors.first('instagram')"
                        />
                        <base-input
                            v-model="profile.linkedin"
                            prepend-icon="fab fa-linkedin-in"
                            name="linkedin"
                            placeholder="Linkedin"
                            :error="errors.first('linkedin')"
                        />
                        <base-input
                            v-model="profile.twitter"
                            prepend-icon="fab fa-twitter"
                            name="twitter"
                            placeholder="Twitter"
                            :error="errors.first('twitter')"
                        />
                        <base-input
                            v-model="profile.website"
                            prepend-icon="fas fa-globe"
                            name="website"
                            placeholder="Site"
                            :error="errors.first('website')"
                        />
                        </div>
                    </section>

                    <section class="nch-profile__danger">
                        <div>
                            <strong>{{ $t('profile.delete_account') }}</strong>
                            <p class="mb-0">{{ $t('profile.recover_warning') }}</p>
                        </div>
                        <base-button
                            @click="deleteAccountModal = true"
                            type="danger"
                            outline
                            size="sm"
                        >
                            {{ $t('common.remove') }}
                        </base-button>
                    </section>
                </div>
            </div>

            <modal v-model:show="resetDialog">
                <ResetPassword
                    dialog
                    @close="resetDialog = false"
                    :user="selectedUser"
                    :color="color"
                />
            </modal>

            <modal v-model:show="deleteAccountModal">
                <p class="text-center">
                    <b>{{ $t('common.attention') }}</b> <br />
                    {{ $t('profile.delete_warning') }}
                    <b>{{ $t('profile.recover_warning') }}</b> <br />
                    {{ $t('profile.want_continue') }}
                </p>
                <div class="d-flex justify-content-center">
                    <base-button
                        @click="deleteAccountModal = false"
                        type="danger"
                    >
                        {{ $t('common.cancel') }}
                    </base-button>
                    <base-button @click.stop="deleteAccount" type="success">
                        {{ $t('common.confirm') }}
                    </base-button>
                </div>
            </modal>

            <modal v-model:show="newAddress">
                <AddressForm v-if="newAddress" profile v-model="address" />
                <base-button block type="success" @click="saveNewAddress"
                    >Salvar</base-button
                >
            </modal>
        </div>
    </div>
</template>

<script>
import Tabs from '../argon/components/Tabs/Tabs';
import TabPane from '../argon/components/Tabs/Tab';
import BaseInput from '../argon/components/Inputs/BaseInput';
import FileInput from '../argon/components/Inputs/FileInput';
import BaseButton from '../argon/components/BaseButton';
import Modal from '../argon/components/Modal';
import BasePagination from '../argon/components/BasePagination';

import ProfileOwner from './ProfileComponents/ProfileOwner';
import ProfileSecondary from './ProfileComponents/ProfileSecondary';
import PhotoSelector from './ProfileComponents/ProfilePhotoSelector';
import ResetPassword from './Auth/ResetPassword';
import DocumentInput from './Common/DocumentInput';
import FormBuilder from '../components/FormBuilderComponents/FormBuilder';
import AddressForm from '../components/FormBuilderComponents/ContentComponents/AddressForm';
import ProfileAddresses from '../components/ProfileComponents/ProfileAddresses';

import axios from 'axios';

export default {
    name: 'Profile',
    components: {
        Tabs,
        TabPane,
        BaseInput,
        FileInput,
        BaseButton,
        Modal,
        ProfileOwner,
        ProfileSecondary,
        PhotoSelector,
        ResetPassword,
        DocumentInput,
        FormBuilder,
        AddressForm,
        ProfileAddresses,
        BasePagination,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        color: {
            type: Object,
            required: true,
        },
        documents: Array,
    },
    data() {
        return {
            owner: this.data.owner,
            secondarys: this.data.secondarys,
            profile: this.data.profile,
            student: this.data.student,
            form: this.data.form,
            formsNew: this.data.form,
            blockStudentFields: this.data.block_student_fields,
            photo: null,
            resetDialog: false,
            deleteAccountModal: false,
            selectedUser: {},
            errorsOwner: 0,
            errorsSecondary: 0,
            newAddress: false,
            edit: false,
            addressId: null,
            addresses: [],
            meta: {},
            links: [],
            address: {
                name: '',
                location: {
                    zip: '',
                    address: '',
                    number: '',
                    neighborhood: '',
                    country: '',
                    state: '',
                    city: '',
                    info: '',
                },
            },
        };
    },
    computed: {
        disableSubmitButton() {
            return !!(
                this.errors.first.length &&
                this.errorsSecondary &&
                this.errorsOwner
            );
        },
        disbaleRemoveAddress() {
            if (this.meta.total <= 1) return true;
            return false;
        },
        isValid() {
            return this.$store.getters.FORM_VALID;
        },
    },
    methods: {
        async removeAddress(id) {
            await window.axios
                .delete(`/api/app/addresses/delete/${id}`)
                .then((res) => {
                    this.$notify({
                        type: 'success',
                        message: res.data.message,
                    });
                    this.getAddresses();
                })
                .catch((err) =>
                    this.$notify({
                        type: 'danger',
                        message: err.response.data.errors,
                    })
                )
                .finally(() => this.$loading.close());
        },
        async saveAddress(data) {
            await this.$store.commit('VALIDATE', true);
            await this.$validator.validate().then(async (valid) => {
                if (valid && this.isValid) {
                    await window.axios
                        .put(`/api/app/addresses/update/${data.id}`, {
                            ...data,
                        })
                        .then((res) => {
                            this.$notify({
                                type: 'success',
                                message: res.data.message,
                            });
                            this.edit = false;
                            this.getAddresses();
                        })
                        .catch((err) =>
                            this.$notify({
                                type: 'danger',
                                message: err.response.data.errors,
                            })
                        )
                        .finally(() => this.$loading.close());
                }
            });
            await this.$store.commit('VALIDATE', false);
        },
        editAddress(id) {
            this.addressId = id;
            this.edit = true;
        },
        removeUser(value) {
            this.secondarys.splice(
                this.secondarys.findIndex((i) => i.id === value),
                1
            );
        },
        createNewSecondary() {
            this.secondarys.unshift({});
        },
        async submit() {
            await this.$store.commit('VALIDATE', true);
            this.$validator.validate().then(async (valid) => {
                if (valid && !this.errorsOwner && !this.errorsSecondary) {
                    this.$loading.show();
                    window.axios
                        .put('/api/app/profile', {
                            owner: this.owner,
                            secondarys: this.secondarys,
                            profile: this.profile,
                            student: this.student,
                            photo: this.photo,
                            form: this.formsNew,
                        })
                        .then((res) =>
                            this.$notify({
                                type: 'success',
                                message: res.data.message,
                            })
                        )
                        .catch((err) =>
                            this.displayErrorMessages(err.response.data.errors)
                        )
                        .finally(() => this.$loading.close());
                }
            });
            await this.$store.commit('VALIDATE', false);
        },
        async deleteAccount() {
            window.axios
                .delete(`/api/app/profile/selfdelete`)
                .then((res) =>
                    this.$notify({
                        type: 'success',
                        message: res.data.message,
                    })
                )
                .catch((err) =>
                    this.displayErrorMessages(err.response.data.errors)
                )
                .finally(() => {
                    location.href = '/';
                });
        },
        displayErrorMessages(errors) {
            if (Array.isArray(errors)) {
                return errors.map((error) => {
                    this.$notify({
                        type: 'danger',
                        message: error,
                    });
                });
            }

            return this.$notify({
                type: 'danger',
                message: errors,
            });
        },
        changePhoto(image) {
            this.photo = {
                status: 1,
                author_id: this.owner.id,
                ...image,
            };
        },
        changePassword(user) {
            this.selectedUser = user;
            this.resetDialog = true;
        },
        countErrorsSecondary(errors) {
            if (errors === 0) {
                this.errorsSecondary = errors;
            } else {
                this.errorsSecondary += errors;
            }
        },
        async getAddresses() {
            await window.axios
                .get(
                    `/api/app/addresses/list/${this.data.profile.student_id}`,
                    {
                        params: {
                            page: this.meta ? this.meta.current_page : 1,
                            search: '',
                            per_page: 5,
                        },
                    }
                )
                .then((res) => {
                    this.meta = res.data.meta;
                    this.links = res.data.links;
                    this.addresses = res.data.data;
                })
                .catch((err) =>
                    this.$notify({
                        type: 'danger',
                        message: err.response.data.errors,
                    })
                )
                .finally(() => this.$loading.close());
        },
        async saveNewAddress() {
            await this.$store.commit('VALIDATE', true);
            await this.$validator.validate().then(async (valid) => {
                if (valid && this.isValid) {
                    await window.axios
                        .post('/api/app/addresses/save', {
                            name: this.address.name,
                            student_id: this.data.profile.student_id,
                            location: this.address.location,
                        })
                        .then((res) => {
                            this.$notify({
                                type: 'success',
                                message: res.data.message,
                            });
                            this.getAddresses();
                            this.address.name = '';
                            this.address.location = {};
                            this.newAddress = false;
                        })
                        .catch((err) =>
                            this.$notify({
                                type: 'danger',
                                message: err.response.data.errors,
                            })
                        )
                        .finally(() => this.$loading.close());
                }
            });
            await this.$store.commit('VALIDATE', false);
        },
        onChangePage(page) {
            this.meta.current_page = page;
            this.getAddresses();
        },
    },
    mounted() {
        this.getAddresses();
    },
};
</script>

<style scoped>
.nch-profile__pagehead {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
}

.nch-profile__pagetitle {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #0f172a;
    margin-bottom: 0.15rem;
}

.nch-profile__pagesub {
    font-size: 0.85rem;
    color: #64748b;
    margin: 0;
}

.nch-profile__save {
    border-radius: 10px;
    padding: 0.6rem 1.5rem;
    font-weight: 700;
}

.nch-profile__card {
    background: #fff;
    border: 1px solid #e8edf3;
    border-radius: 14px;
    margin-bottom: 1.25rem;
    overflow: hidden;
}

.nch-profile__cardhead {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.9rem 1.25rem;
    border-bottom: 1px solid #eef2f7;
    font-weight: 700;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
}

.nch-profile__cardhead i {
    color: #94a3b8;
}

.nch-profile__card--center .nch-profile__cardhead {
    justify-content: center;
}

.nch-profile__cardbody {
    padding: 1.25rem;
}

.nch-profile__danger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    background: #fff5f6;
    border: 1px solid #fcd6dc;
    border-radius: 14px;
    padding: 1rem 1.25rem;
    color: #8a2432;
    font-size: 0.85rem;
    margin-bottom: 1.25rem;
}
</style>
